import { type CSSProperties, type ElementType, memo, type ReactNode, useMemo } from 'react';
import { clsx } from 'clsx';
import type { Color, TextTone } from '../../styles/tokens';
import { buildResponsiveProps, buildResponsiveVariableValue, type ResponsiveValue } from '../../util/responsive';
import { sanitizeCSSVariables } from '../../util/style';
import type { Polymorphic } from '../polymorphism/polymorphic';
import { TypographyProvider, useMergedTypography } from './context';
import styles from './typography.module.scss';

type TypographyElement = 'div' | 'code' | 'span' | 'p' | 'strong' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label' | 'sup' | 'time' | 'legend';

export const DEFAULT_TYPOGRAPHY_ELEMENT: TypographyElement = 'span';

export type DefaultTypographyElement = typeof DEFAULT_TYPOGRAPHY_ELEMENT;

type TypographyVariant = 'mono' | 'main' | 'heading' | 'inherit';

type TypographyAlignment = 'left' | 'center' | 'right' | 'inherit';

type TypographyTransform = 'uppercase' | 'inherit';

type TypographyDecoration = 'strikethrough' | 'underline' | 'inherit' | 'none';

type TypographyWrap = 'pre-line' | 'no-wrap' | 'break-word' | 'ellipsis' | 'anywhere' | 'inherit' | 'preserve';

export type TypographyTone = TextTone | Color | 'inherit';

function getDefaultVariant(as: TypographyElement): TypographyVariant {
  switch (as) {
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6':
      return 'heading';
    case 'code':
      return 'mono';
    case 'p':
    case 'span':
    case 'strong':
    case 'sup':
    case 'time':
    default:
      return 'main';
  }
}

export type TypographySize = `${number}rem` | `${number}px` | 'inherit' | number; // If number, it will be treated as rem

export type TypographyMainWeight = 'regular' | 'medium' | 'bold' | number;

export type TypographyMonoWeight = 'medium' | 'semi-bold' | 'bold' | number;

export type TypographyHeadingWeight = 'semi-bold' | 'bold' | number;

type TypographyWeight = TypographyMainWeight | TypographyMonoWeight | TypographyHeadingWeight | 'inherit';

export function buildTypographyToneVariable(tone: TypographyTone | undefined) {
  switch (tone) {
    case 'lt':
    case 'md':
    case 'button':
      return `var(--ixf-text-tone-${tone})`;
    case 'inherit':
      return 'inherit';
    default:
      return tone ? `var(--ixf-color-${tone})` : 'inherit';
  }
}

const componentStyleName = 'typography';

export function buildTypographyStyleValue(
  tone: TypographyTone | undefined,
  style: CSSProperties = {},
  variableName: string = '--ixf-typography-color',
): CSSProperties {
  return {
    ...style,
    [variableName]: buildTypographyToneVariable(tone),
  } as CSSProperties;
}

function getDefaultTypographyWeight(variant: TypographyVariant): TypographyWeight {
  switch (variant) {
    case 'main':
      return 'medium';
    case 'mono':
      return 'medium';
    case 'heading':
      return 'bold';
    default:
      return 'medium';
  }
}

export interface BaseTypographyProps {
  align?: TypographyAlignment;
  children?: ReactNode;
  className?: string;
  dateTime?: string;
  decoration?: TypographyDecoration;
  htmlFor?: string;
  letterSpacing?: ResponsiveValue<`${number}px`>;
  lineHeight?: ResponsiveValue<TypographySize>;
  inheritance?: 'inherited' | 'current';
  size?: ResponsiveValue<TypographySize>;
  style?: CSSProperties;
  tone?: TypographyTone;
  transform?: TypographyTransform;
  wrap?: TypographyWrap;
}

export interface MainTypographyProps extends BaseTypographyProps {
  variant?: 'main';
  weight?: ResponsiveValue<TypographyMainWeight>;
}

export interface MonoTypographyProps extends BaseTypographyProps {
  variant?: 'mono';
  weight?: ResponsiveValue<TypographyMonoWeight>;
}

export interface HeadingTypographyProps extends BaseTypographyProps {
  variant?: 'heading';
  weight?: ResponsiveValue<TypographyHeadingWeight>;
}

export type TypographyPropsTypeUnion = MainTypographyProps | MonoTypographyProps | HeadingTypographyProps;

export type TypographyProps<AsComponent extends ElementType = DefaultTypographyElement> = Polymorphic.Props<AsComponent, TypographyPropsTypeUnion>;

export const DEFAULT_HEADING_LEVEL_1_TYPOGRAPHY = {
  variant: 'heading',
  weight: 'bold',
  size: '1.125rem',
  lineHeight: '2rem',
} satisfies TypographyProps;

export const DEFAULT_HEADING_LEVEL_2_TYPOGRAPHY = {
  size: '1rem',
  lineHeight: '1.5rem',
} satisfies TypographyProps;

export const DEFAULT_HEADING_LEVEL_3_TYPOGRAPHY = {
  letterSpacing: '1px',
  weight: 'bold',
  size: '0.75rem',
  lineHeight: '1.5rem',
  transform: 'uppercase',
} satisfies TypographyProps;

export function BaseTypography<AsComponent extends ElementType = DefaultTypographyElement>({
  as,
  children,
  className,
  align,
  decoration,
  letterSpacing,
  lineHeight,
  size,
  style,
  tone = 'md',
  transform,
  variant = 'main',
  weight,
  wrap,
  ...rest
}: TypographyProps<AsComponent>) {
  const Component = (as ?? DEFAULT_TYPOGRAPHY_ELEMENT) satisfies ElementType;
  const usableWeight = weight || getDefaultTypographyWeight(variant);
  const baseStyle = useMemo(
    () =>
      sanitizeCSSVariables(
        buildTypographyStyleValue(tone, {
          ...style,
          ...buildResponsiveProps(componentStyleName, 'letter-spacing', (letterSpacing ?? transform === 'uppercase') ? '1px' : undefined),
          ...buildResponsiveProps(componentStyleName, 'size', size),
          ...buildResponsiveProps(componentStyleName, 'line-height', lineHeight),
          ...buildResponsiveProps(componentStyleName, 'font-weight', usableWeight, undefined, function weightBuilder(weight: TypographyWeight) {
            return ['string', 'number'].includes(typeof weight) && !Number.isNaN(parseInt(weight as string, 10))
              ? weight
              : buildResponsiveVariableValue(weight, `font-weight-${variant}`);
          }),
        } as CSSProperties),
      ),
    [lineHeight, size, style, tone, letterSpacing, transform, usableWeight, variant],
  );

  return (
    <Component
      className={clsx(
        styles.typography,
        styles[`decoration-${decoration}`],
        styles[`variant-${variant || getDefaultVariant(Component as TypographyElement)}`],
        styles[`align-${align}`],
        styles[`as-${Component}`],
        styles[`transform-${transform}`],
        styles[`wrap-${wrap}`],
        className,
      )}
      style={baseStyle}
      {...rest}
    >
      {children}
    </Component>
  );
}

function Typography<AsComponent extends ElementType = DefaultTypographyElement>({
  as,
  children,
  className,
  inheritance = 'inherited',
  style,
  ...props
}: TypographyProps<AsComponent>) {
  const composedProps = useMergedTypography(props, inheritance);
  const Component = (as ?? DEFAULT_TYPOGRAPHY_ELEMENT) satisfies ElementType;

  return (
    <TypographyProvider value={props}>
      <BaseTypography as={Component} className={className} style={style} {...composedProps}>
        {children}
      </BaseTypography>
    </TypographyProvider>
  );
}

function TypographyLevel1<AsComponent extends ElementType = DefaultTypographyElement>(props: TypographyProps<AsComponent>) {
  return <Typography {...DEFAULT_HEADING_LEVEL_1_TYPOGRAPHY} {...props} />;
}

function TypographyLevel2<AsComponent extends ElementType = DefaultTypographyElement>(props: TypographyProps<AsComponent>) {
  return <Typography {...DEFAULT_HEADING_LEVEL_2_TYPOGRAPHY} {...props} />;
}

function TypographyLevel3<AsComponent extends ElementType = DefaultTypographyElement>(props: TypographyProps<AsComponent>) {
  return <Typography {...DEFAULT_HEADING_LEVEL_3_TYPOGRAPHY} {...props} />;
}

Typography.Level1 = memo(TypographyLevel1);
Typography.Level2 = memo(TypographyLevel2);
Typography.Level3 = memo(TypographyLevel3);

export { Typography };
